import { Charger, ParkingZoneType } from 'types/common';
import { create } from 'zustand';

/**
 * Currently selected element on the map / in the park flow (parkZone,
 * streetpark or other marker). `activeElementSecondaryUid` is used when the
 * type is a charging station ("CHARGING") and then holds the charging station
 * id. `activeCharger` is the individual charger the user picked inside that
 * station.
 *
 * Not persisted: selection is per-session UI state and must not survive a
 * relaunch.
 */
type ActiveElementState = {
  activeElement: string;
  activeElementType: ParkingZoneType;
  activeElementSecondaryUid: string;
  activeCharger: Charger | undefined;
};

type ActiveElementActions = {
  setActiveElement: (parkingZoneUid: string) => void;
  setActiveElementType: (type: ParkingZoneType) => void;
  setActiveElementSecondaryUid: (secondaryUid: string) => void;
  setActiveCharger: (charger: Charger | undefined) => void;
  /**
   * Sets the whole selection in one update so the 26+ subscribers re-render
   * once instead of three times. Called with no arguments this is the
   * deselect path (see `setParkingZoneReactiveVars` in mapUtils).
   * Deliberately leaves `activeCharger` untouched, matching the previous
   * reactive-var behaviour.
   */
  setActiveElementSelection: (
    parkingZoneUid?: string,
    type?: ParkingZoneType,
    secondaryUid?: string
  ) => void;
  /**
   * Changes what kind of element is selected and which charging station in it,
   * without touching which zone is selected. One update so consumers of both
   * (useChargingStation, Tariff) re-render once.
   */
  setActiveElementTypeAndStation: (
    type: ParkingZoneType,
    secondaryUid: string
  ) => void;
};

export const useActiveElementStore = create<
  ActiveElementState & ActiveElementActions
>()((set) => ({
  activeElement: '',
  activeElementType: '',
  activeElementSecondaryUid: '',
  activeCharger: undefined,
  setActiveElement: (parkingZoneUid) => set({ activeElement: parkingZoneUid }),
  setActiveElementType: (type) => set({ activeElementType: type }),
  setActiveElementSecondaryUid: (secondaryUid) =>
    set({ activeElementSecondaryUid: secondaryUid }),
  setActiveCharger: (charger) => set({ activeCharger: charger }),
  setActiveElementSelection: (
    parkingZoneUid = '',
    type = '',
    secondaryUid = ''
  ) =>
    set({
      activeElement: parkingZoneUid,
      activeElementType: type,
      activeElementSecondaryUid: secondaryUid,
    }),
  setActiveElementTypeAndStation: (type, secondaryUid) =>
    set({
      activeElementType: type,
      activeElementSecondaryUid: secondaryUid,
    }),
}));
